import { defineStore } from 'pinia'

import type { ProfileBaseInfo, ProfileWallet } from '@/modules/profile/types/profile'
import { loadProfileBaseInfo } from '@/modules/profile/services/profileService'
import { ProfileApi } from '@/modules/profile/api/profileApi'
import { httpClient, handleUnauthorized } from '@/shared/api/httpClient'
import {
  clearProfileCache,
  readProfileCache,
  writeProfileCache,
  type ProfileCacheEntry,
} from '@/modules/profile/services/profileCache'
import { createTokenStorage } from '@space/shared-api-client'
import { isNetworkError } from '@/shared/utils/networkErrorDetection'
import { i18n } from '@/i18n'
import {
  canRedirectToTmaInit,
  tmaAddBreadcrumb,
  getAuthStateContext,
} from '@/mini-app/tmaDebugLogger'

const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'
const tokenStorage = createTokenStorage({ storageKey })

interface ProfileState {
  profile: ProfileBaseInfo | null
  /**
   * Кто вошёл по данным прошлого визита — чтобы шапка и вкладки нарисовались
   * сразу, не дожидаясь `base-info`. Это ускорение показа, а не источник
   * правды: допуск решается токеном и ответом сервера (см. profileCache.ts).
   */
  cachedProfile: ProfileCacheEntry | null
  loading: boolean
  error: string | null
}

interface LoginSuccessPayload {
  success: number
  location: string
  user_id: number
  // 2FA fields
  requires_approval?: boolean
  approval_type?: 'otp' | 'totp'
  approval_methods?: string[]
  session_id?: number
  // SSO field
  sso_redirect?: string
}

interface LoginSuccessResponse {
  result: 'success'
  data: LoginSuccessPayload
}

interface LoginErrorResponse {
  result: 'error'
  error_code?: number
  error_description?: string
  error_details?: string
  error_message?: string
}

type LoginResponse = LoginSuccessResponse | LoginErrorResponse

type LoginPayload = Record<string, string | number | boolean>

interface LoginApprovalPayload {
  code: string
  redirect?: string
  referer?: string
}

interface SignupResponse {
  result?: string
  data?: {
    success: number
    redirect?: string
    user_id?: number
    otp_required?: number
    session?: string
    reciever?: 'email' | 'telegram'
    reciever_address?: string
    /**
     * Спринт 14 — фаза выбора: код НЕ отправлен, бэкенд ждёт ответа
     * «куда прислать». `channels[0]` — предвыбранный канал.
     */
    choose?: { channels: string[]; captcha_session?: string }
  }
}

/**
 * OTP-R3: busy регистрации снимает только её последний владелец — поздний
 * finally отменённой попытки не должен гасить loading новой.
 */
let registerOwner: object | null = null

/** Технический адрес регистрации через Telegram: `<login>.tg<id>@telegram.org`. */
export const isTelegramTechnicalEmail = (email: string): boolean => /@telegram\.org$/i.test(email.trim())

export const useProfileStore = defineStore('profile', {
  state: (): ProfileState => ({
    profile: null,
    cachedProfile: readProfileCache(),
    loading: false,
    error: null,
  }),
  getters: {
    /**
     * Рисовать ли кабинет как свой. До ответа `base-info` отвечает кеш —
     * поэтому нет мигания «войти» → аккаунт. Доступ этим НЕ решается: роутер
     * пускает по токену, а протухшую сессию снимает ответ сервера.
     */
    isAuthenticated(state): boolean {
      return !!state.profile || !!state.cachedProfile
    },
    /** Почта для шапки: свежая, а до ответа — из кеша. */
    displayEmail(state): string {
      return state.profile?.email ?? state.cachedProfile?.email ?? ''
    },
    fullName(state): string {
      const source = state.profile ?? state.cachedProfile
      if (!source) {
        return ''
      }

      return `${source.first_name} ${source.last_name}`.trim()
    },
    /**
     * Подпись «кто вошёл» для шапки: имя, а без имени — почта или логин.
     *
     * Аккаунт из Telegram живёт с техническим адресом `tg<id>@telegram.org`
     * (решение оператора 2026-09-22) — это служебная строка, а не то, как
     * человек себя знает. Показывать её в шапке нельзя: вместо неё логин
     * (по умолчанию — username из Telegram).
     */
    displayName(): string {
      const source = this.profile ?? this.cachedProfile
      if (!source) {
        return ''
      }

      const name = this.fullName
      if (name) {
        return name
      }

      const email = source.email ?? ''
      return email && !isTelegramTechnicalEmail(email) ? email : (source.login ?? '')
    },
    primaryWallet(state): ProfileWallet | null {
      return state.profile?.wallet?.[0] ?? null
    },
    walletBalanceLabel(): string {
      return this.primaryWallet?.amount_string ?? '0'
    },
    avatarUrl(state): string | null {
      // Return state profile photo first (reactive), fallback to localStorage for immediate display
      return state.profile?.photo ?? state.cachedProfile?.photo ?? localStorage.getItem('user_avatar') ?? null
    },
    isAmbassador(state): boolean {
      if (state.profile?.career?.is_ambassador !== undefined) {
        return state.profile.career.is_ambassador === true
      }
      // Fallback to cached value before base-info loads
      return localStorage.getItem('is_ambassador') === 'true'
    },
    referralLink(state): string {
      return state.profile?.reflink ?? state.profile?.referral_link ?? state.profile?.referal_link ?? ''
    },
    hasCountry(state): boolean {
      return !!state.profile?.country?.code
    },
    /** HK-IGRA.95: правда о привязке Telegram — одна, из base-info
     * (`telegram.connected`). Поле исторически бывает и строкой (старый
     * контракт account-движка), поэтому объектная форма проверяется явно. */
    telegramConnected(state): boolean {
      const telegram = state.profile?.telegram
      return typeof telegram === 'object' && telegram !== null ? telegram.connected === true : false
    },
    /** Имя бота сайта (`wp_sites.telegram_bot_name`, с «@»). Нужно, чтобы
     * собрать ссылку на бота при привязке аккаунта. */
    telegramBot(state): string | null {
      const telegram = state.profile?.telegram
      return typeof telegram === 'object' && telegram !== null ? telegram.bot ?? null : null
    },
  },
  actions: {
    async fetchProfile(force = false) {
      if (this.loading) {
        return
      }

      if (!force && this.profile) {
        return
      }

      this.loading = true
      this.error = null

      try {
        const profile = await loadProfileBaseInfo()
        const cached = writeProfileCache(profile)

        // Пустой профиль (ответ без id) — не доказательство того, что человек
        // вошёл. Оставить нарисованный по кешу кабинет здесь нельзя: это показ
        // устаревших данных под видом текущих.
        if (!cached) {
          this.dropAuthenticatedState()
          return
        }

        this.profile = profile
        this.cachedProfile = cached

        // Cache avatar URL
        if (this.profile?.photo) {
          localStorage.setItem('user_avatar', this.profile.photo)
        } else {
          // Should we remove it if it's null? Maybe, but let's prevent flickering first. 
          // If API returns null, we should probably respect that eventually.
          if (this.profile && this.profile.photo === null) {
            localStorage.removeItem('user_avatar')
          }
        }

        // Cache ambassador status
        const isAmbassador = this.profile?.career?.is_ambassador
        if (isAmbassador !== undefined) {
          localStorage.setItem('is_ambassador', String(isAmbassador))
        }

      } catch (error: any) {
        // TMA mode: if base-info returns error_code 100 (not authenticated),
        // redirect to /tma-init for re-authentication
        const errorCode = error?.errorCode ?? error?.response?.data?.error_code
        const isTmaUser = document.cookie.split('; ').some(c => c === 'tma_user=1')
        if (errorCode === 100 && isTmaUser) {
          // --- Debug: log re-auth attempt with full context ---
          tmaAddBreadcrumb('profileStore: error_code 100 + tma_user cookie — re-auth triggered', {
            errorCode,
            ...getAuthStateContext(),
            error_message: error?.message ?? error?.response?.data?.error_description ?? null,
          }, 'warning')

          // Сессии нет — кеш «кто вошёл» протух в обеих ветках ниже. Токен и
          // переход здесь не трогаем: этим занимается сам TMA-сценарий.
          this.cachedProfile = null
          clearProfileCache()

          // --- Loop protection ---
          if (canRedirectToTmaInit()) {
            console.warn('[GonkaGame][profile] TMA auth expired (error_code: 100) — redirecting to /tma-init')
            this.error = 'tma_auth_expired'
            this.profile = null
            window.location.href = '/tma-init'
          } else {
            // Loop detected — DO NOT redirect, show error instead
            console.error('[GonkaGame][profile] TMA re-auth loop detected — NOT redirecting')
            this.error = 'tma_auth_loop'
            this.profile = null
            // Emit event so UI can show a meaningful error
            window.dispatchEvent(new CustomEvent('gonkagame:connection-error'))
          }
          return
        }

        // Обычный вход (не TMA): `error_code: 100` — ACCOUNT_NEED_AUTH, сервер
        // говорит, что сессии больше нет. Раньше это попадало в ветку «сервер
        // барахлит» и кабинет оставался на экране; с кешем так остались бы
        // видны старые данные. Выкидываем из аккаунта.
        if (errorCode === 100) {
          this.dropAuthenticatedState()
          return
        }

        if (isNetworkError(error)) {
          // Network error: preserve last known profile state and show modal
          console.warn('[GonkaGame][profile] Network error during profile load - preserving state', error)
          this.error = 'network_error'
          // Emit event for connection error modal
          window.dispatchEvent(new CustomEvent('gonkagame:connection-error'))
        } else {
          // Check if this is a 401 Unauthorized response - only then should we clear profile
          const status = error?.response?.status ?? error?.status
          if (status === 401) {
            console.warn('[GonkaGame][profile] Unauthorized (401) - clearing profile', error)
            // Снятие кеша и токена — здесь, а не только в перехватчике 401:
            // иначе шапка осталась бы «своей» на данных прошлой сессии.
            this.dropAuthenticatedState()
          } else {
            // Server error (5xx) or other: preserve last known profile state
            console.warn('[GonkaGame][profile] Server error during profile load - preserving state', error)
            this.error = 'server_error'
            // Emit event for connection error modal - same behavior as network errors
            window.dispatchEvent(new CustomEvent('gonkagame:connection-error'))
          }
        }
      } finally {
        this.loading = false
      }
    },

    /**
     * Сервер ответил «не авторизован»: снимаем кеш, токен и профиль и уводим на
     * гостевой экран. Токен, прочие ключи и сам переход — в `handleUnauthorized`
     * (он же знает, на каких публичных страницах уводить не надо).
     */
    dropAuthenticatedState() {
      this.profile = null
      this.cachedProfile = null
      this.error = 'unauthorized'
      handleUnauthorized()
    },

    async updateCountry(countryCode: string): Promise<void> {
      const response = await httpClient.post<{ result: string; country?: { code: string; name: string } }>(
        '/profile/update-country',
        { country_code: countryCode }
      )

      if (response.result === 'success' && response.country && this.profile) {
        this.profile.country = response.country
      }
    },

    async login(payload: LoginPayload): Promise<LoginSuccessPayload> {
      this.loading = true
      this.error = null
      try {
        const data = await httpClient.post<LoginResponse>('/profile/login', payload)

        if (data.result === 'success' && data.data?.success) {
          const storedToken = httpClient.getToken()
          if (storedToken) {
            tokenStorage.setToken(storedToken)
          }

          const requiresApproval = (data.data.location ?? '').includes('login-approval')

          if (!requiresApproval) {
            await this.fetchProfile(true)
          }

          return data.data
        }

        const errorResponse = data as LoginErrorResponse
        const apiError = errorResponse.error_message ?? errorResponse.error_details ?? errorResponse.error_description ?? i18n.global.t('errors.loginFailed')
        this.error = apiError
        throw new Error(apiError)
      } catch (error: any) {
        // Preserve original error message for UI
        const message = error?.message ?? i18n.global.t('errors.loginFailed')
        this.error = message
        // Rethrow original error to preserve response.data for downstream handlers
        throw error
      } finally {
        this.loading = false
      }
    },

    async confirmLoginApproval(payload: LoginApprovalPayload): Promise<LoginSuccessPayload> {
      this.loading = true
      this.error = null
      try {
        const data = await httpClient.post<LoginResponse>('/profile/login-approval', payload)

        if (data.result === 'success' && data.data?.success) {
          const storedToken = httpClient.getToken()
          if (storedToken) {
            tokenStorage.setToken(storedToken)
          }

          await this.fetchProfile(true)
          return data.data
        }

        const errorResponse = data as LoginErrorResponse
        const apiError = errorResponse.error_message ?? errorResponse.error_details ?? errorResponse.error_description ?? i18n.global.t('errors.loginConfirmFailed')
        this.error = apiError
        throw new Error(apiError)
      } catch (error: any) {
        const message = error?.message ?? i18n.global.t('errors.loginConfirmFailed')
        this.error = message
        throw new Error(message)
      } finally {
        this.loading = false
      }
    },

    async register(payload: Record<string, unknown>, isCurrent?: () => boolean) {
      const owner = {}
      registerOwner = owner
      this.loading = true
      this.error = null
      try {
        // OTP-R3: с isCurrent x-token и прочие эффекты ответа применяются
        // только к актуальной попытке входа.
        const response = isCurrent
          ? await httpClient.guardedRequest<SignupResponse>('/profile/signup', isCurrent, { method: 'POST', body: JSON.stringify(payload) })
          : await httpClient.post<SignupResponse>('/profile/signup', payload)

        // Спринт 14: фаза выбора приходит с `success: 1`, но регистрацией не
        // является — кода нет, сессии нет. Без этого возврата ветка успеха
        // ниже пошла бы тянуть профиль неавторизованным запросом и снесла бы
        // сохранённый токен (dropAuthenticatedState в fetchProfile).
        if (response.data?.choose) {
          return response.data
        }

        // HK-IGRA.129: на igra.now signup сам отправил код и вернул OTP-сессию
        // (ScopeAccount::$signup_send_login_code). Сессии пользователя ещё нет
        // — она появится только после /profile/login-by-code/verify, поэтому
        // ни токена, ни профиля здесь не ждём.
        if (response.data?.otp_required === 1) {
          return response.data
        }

        // Check for success in response
        // Token is automatically extracted from x-token header by httpClient
        if (response.data?.success === 1 || response.result === 'success') {
          // Explicitly save the auth token (same as login())
          const storedToken = httpClient.getToken()
          if (storedToken) {
            tokenStorage.setToken(storedToken)
          }

          // Reset loading before fetchProfile to prevent early return
          this.loading = false
          // Fetch profile to fully initialize the user session
          await this.fetchProfile(true)
          return response.data
        } else {
          // If we got a response but it's not success, throw the response for error parsing
          throw response
        }
      } catch (error: any) {
        // Отменённая попытка: ошибку стора новой попытки не трогаем.
        if (isCurrent && !isCurrent()) throw error
        this.error = error.response?.data?.message || i18n.global.t('errors.registrationFailed')
        throw error
      } finally {
        if (registerOwner === owner) {
          registerOwner = null
          this.loading = false
        }
      }
    },

    async searchSponsor(payload: Record<string, unknown>) {
      try {
        return await httpClient.post('/profile/search-sponsor', payload)
      } catch (error) {
        console.error('Sponsor search failed', error)
        throw error
      }
    },

    async updateContacts(payload: Parameters<typeof ProfileApi.updateContacts>[0]) {
      this.loading = true
      this.error = null
      try {
        await ProfileApi.updateContacts(payload)
        await this.fetchProfile(true) // Refresh profile data
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.contactsUpdateFailed')
        throw error
      } finally {
        this.loading = false
      }
    },

    async initSession(actionName: string, payload: Record<string, any> = {}) {
      this.loading = true
      this.error = null
      try {
        return await ProfileApi.initActionSession(actionName, payload)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.sessionInitFailed')
        throw error
      } finally {
        this.loading = false
      }
    },

    async changePassword(payload: Parameters<typeof ProfileApi.changePassword>[0]) {
      this.loading = true
      this.error = null
      try {
        await ProfileApi.changePassword(payload)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.passwordChangeFailed')
        throw error
      } finally {
        this.loading = false
      }
    },

    async changePayPassword(payload: Parameters<typeof ProfileApi.changePayPassword>[0]) {
      this.loading = true
      this.error = null
      try {
        await ProfileApi.changePayPassword(payload)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.walletPasswordChangeFailed')
        throw error
      } finally {
        this.loading = false
      }
    },

    async uploadAvatar(file: File) {
      this.loading = true
      this.error = null
      try {
        await ProfileApi.uploadAvatar(file)

        // 1. Fetch latest profile to ensure other data is synced
        await this.fetchProfile(true)

        // 2. Optimistically update with the uploaded file to prevent caching issues
        // The backend might return a cached URL or old URL for a few seconds
        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target?.result as string
          if (result && this.profile) {
            this.profile.photo = result
            localStorage.setItem('user_avatar', result)
          }
        }
        reader.readAsDataURL(file)

      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.photoUploadFailed')
        throw error
      } finally {
        this.loading = false
      }
    },

    async updatePaymentMethods(payload: Parameters<typeof ProfileApi.updatePaymentMethods>[0]) {
      this.loading = true
      this.error = null
      try {
        await ProfileApi.updatePaymentMethods(payload)
        await this.fetchProfile(true)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.paymentDetailsSaveFailed')
        throw error
      } finally {
        this.loading = false
      }
    },

    async updateSettings(payload: Parameters<typeof ProfileApi.updateSettings>[0]) {
      this.loading = true
      this.error = null
      try {
        await ProfileApi.updateSettings(payload)
        await this.fetchProfile(true)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.settingsSaveFailed')
        throw error
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        const response = await httpClient.delete<{ success: boolean; sso_redirect?: string }>('/profile/logout')

        // If SSO redirect is provided, redirect to SSO logout first
        if (response.sso_redirect) {
          window.location.href = response.sso_redirect
          return
        }
      } catch (error) {
        console.error('Logout API failed', error)
      } finally {
        // Clear authentication token
        tokenStorage.setToken('')

        // Clear profile state
        this.profile = null
        this.cachedProfile = null
        clearProfileCache()

        // Clear cached items
        localStorage.removeItem('user_avatar')
        localStorage.removeItem('partner_bonus_split')
        localStorage.removeItem('is_ambassador')
        localStorage.removeItem(storageKey)

        // Clear matrix module localStorage cache (CR-MTX-07)
        Object.keys(localStorage)
          .filter(key => key.startsWith('/api/matrix/'))
          .forEach(key => localStorage.removeItem(key))

        // Clear auth cookie
        document.cookie = 'dasdasdaddjdj=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'

        // Redirect to landing only if no SSO redirect
        window.location.href = '/'
      }
    },

    async sendEmailVerificationCode(email: string) {
      return await ProfileApi.sendEmailVerificationCode(email)
    },

    async changeEmail(payload: { email: string; code: string; confirmAction?: string; confirmActionCode?: string }) {
      this.loading = true
      this.error = null
      try {
        await ProfileApi.changeEmail(payload)
        await this.fetchProfile(true)
      } catch (error: any) {
        this.error = error.message || i18n.global.t('errors.emailChangeFailed')
        throw error
      } finally {
        this.loading = false
      }
    },

    async setMfAuthorization(
      enabled: boolean,
      options?: { confirmAction?: string; confirmActionCode?: string }
    ): Promise<void> {
      this.loading = true
      this.error = null
      try {
        const response = await ProfileApi.setMfAuthorization(enabled, options)
        // Update local state from API response
        if (this.profile?.security) {
          this.profile.security.mf_authorization = response.enabled
        }
      } catch (error: any) {
        this.error = error.message || 'Error changing 2FA settings'
        throw error
      } finally {
        this.loading = false
      }
    }
  },
})


